import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TitleTypography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import { styled } from '@mui/material/styles';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RssFeedRoundedIcon from '@mui/icons-material/RssFeedRounded';
import { Upvote } from './Upvote';
import { SubmitTileForm } from './SubmitTileForm';

async function fetchTileContent() {
  const SPACE_ID   = 'zw84cuagwed8';
  const ACCESS_TOKEN = 'V5ofoTzcdDzxrgiKxseGpE-klKXNizC-B--eXor9aI0';
  const url = `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/master/entries?access_token=${ACCESS_TOKEN}&content_type=contentTile&include=3`;
  const resp = await fetch(url);
  console.log(resp);
  return resp.json();
}

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  height: '100%',
  backgroundColor: (theme.vars || theme).palette.background.paper,
  '&:hover': {
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  '&:focus-visible': {
    outline: '3px solid',
    outlineColor: 'hsla(210, 98%, 48%, 0.5)',
    outlineOffset: '2px',
  },
}));

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 16,
  flexGrow: 1,
  '&:last-child': {
    paddingBottom: 16,
  },
});

const StyledTypography = styled(Typography)({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

function Author({ authors, time }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
      }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}
      >
        <AvatarGroup max={3}>
          {authors.map((author, index) => (
            <Avatar
              key={index}
              alt={author.name}
              src={author.avatar}
              sx={{ width: 24, height: 24 }}
            />
          ))}
        </AvatarGroup>
        <Typography variant="caption">
          {authors.map((author) => author.name).join(', ')}
        </Typography>
      </Box>
      <Typography variant="caption">{time}</Typography>
    </Box>
  );
}

Author.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
};


export function Search() {
  return (
    <FormControl sx={{ width: { xs: '100%', md: '25ch' } }} variant="outlined">
      <OutlinedInput
        size="small"
        id="search"
        placeholder="Search…"
        sx={{ flexGrow: 1 }}
        startAdornment={
          <InputAdornment position="start" sx={{ color: 'text.primary' }}>
            <SearchRoundedIcon fontSize="small" />
          </InputAdornment>
        }
        inputProps={{
          'aria-label': 'search',
        }}
      />
    </FormControl>
  );
}

export default function MainContent() {
  const [focusedCardIndex, setFocusedCardIndex] = React.useState(null);
  const [tiles, setTiles] = useState([]);
  const [includes, setIncludes] = useState({ Entry: [], Asset: [] });

  const handleFocus = (index) => {
    setFocusedCardIndex(index);
  };

  const handleBlur = () => {
    setFocusedCardIndex(null);
  };

  const handleClick = () => {
    console.info('You clicked the filter chip.');
  };

  const reloadTiles = () => {
    fetchTileContent().then(data => {
      setTiles(data.items);
      setIncludes(data.includes || {});
    });
  };
    
  useEffect(() => {
    reloadTiles();
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography variant="h2">Proposed Times</Typography>
      <Grid container spacing={4} columns={12}>
        {tiles.map(tile => {
          const { sys, fields } = tile;

          // 0️⃣ pull out the "tags"

          // 1️⃣ pull out your "content" links
          const contentLinks = fields.content || [];

          // 2️⃣ find a mediaBlock link (if any)
          const mediaLink = contentLinks.find(link =>
            includes.Entry.find(e =>
              e.sys.id === link.sys.id &&
              e.sys.contentType.sys.id === 'mediaBlock'
            )
          );

          // 3️⃣ resolve that mediaBlock → Entry → Asset → URL
          let imgUrl;
          let isVideo = false;
          if (mediaLink) {
            const mediaEntry = includes.Entry.find(e => e.sys.id === mediaLink.sys.id);
            const assetId = mediaEntry?.fields.asset.sys.id;
            const asset = includes.Asset.find(a => a.sys.id === assetId);
            imgUrl = asset && `https:${asset.fields.file.url}`;
            isVideo = asset?.fields?.file?.contentType?.includes('video');
          }

          // 4️⃣ later you could also render any richText links:
          const richTextBodies = contentLinks
            .map(link => includes.Entry.find(e => e.sys.id === link.sys.id))
            .filter(e => e?.sys.contentType.sys.id === 'richText')
            .map(e => e.fields.body);

            return (
              <Grid key={sys.id} size={{ xs: 12, md: 6 }}>

              <StyledCard
                variant="outlined"
                sx={{
                  position: 'relative',
                }}
                size="small"
              >
                <Upvote initialCount={0} sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 1000,
                }}/>
                {imgUrl && (
                  <CardMedia
                    component={isVideo ? 'video' : 'img'}
                    {...(isVideo
                      ? {
                          src: imgUrl,
                          controls: true,
                          autoPlay: true,
                          loop: true
                        }
                      : {
                          image: imgUrl,
                          alt: fields.title
                        })}
                    sx={{
                      aspectRatio: '16 / 9',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                )}

                <StyledCardContent>

                <TitleTypography variant="h6" gutterBottom>
                  {fields.title}
                </TitleTypography>

                {richTextBodies.map((body, i) => (
                  <StyledTypography key={i} variant="body2" color="text.secondary" gutterBottom>
                    {body}
                  </StyledTypography>
                ))}
                </StyledCardContent>

                <Author authors={fields.authors || []} time={fields.time} />
              </StyledCard>
              </Grid>
            );       
        })}
      </Grid>
    </Box>
  );
}
